import { LocationData } from '../../models/location-data';
import { intersection } from 'lodash';

export const filterGbpLocationIdsByCountryCodes = (
    locationDataList: LocationData[], countryCodes: string[]) => locationDataList
    .filter(locationData => countryCodes
        .includes(locationData.country))
    .map(location => location.gbpLocationId);

export const filterGbpLocationIdsByByStoreCodes = (
    locationDataList: LocationData[], storeCodes: string[]) => locationDataList
    .filter(locationData => storeCodes
        .includes(locationData.storeCode))
    .map(location => location.gbpLocationId);

export const filterGbpLocationIdsByBusinessNames = (
    locationDataList: LocationData[], businessNames: string[]) => locationDataList
    .filter(locationData => businessNames
        .includes(locationData.businessName))
    .map(location => location.gbpLocationId);

export const filterGbpLocationIdsByCities = (
    locationDataList: LocationData[], cities: string[]) => locationDataList
    .filter(locationData => cities
        .includes(locationData.city))
    .map(location => location.gbpLocationId);

export const filterGbpLocationIdsByByGmbAccountIds = (
    locationDataList: LocationData[], gmbAccountIds: string[]) => locationDataList
    .filter(locationData =>
        // For each listing, check if ANY of its myBAccountDtos
        locationData.myBAccountDtos.some(listingLocationGroup =>
            // has a gmbAccountId that exists in the list of provided location group IDs
            gmbAccountIds.includes(listingLocationGroup.gmbAccountId)),
    ).map(location => location.gbpLocationId);

/**
 * Finds the intersection (logical AND) of a list of arrays,
 * automatically ignoring any input arrays that are empty.
 * @param filters - An array of arrays, where each inner array is a filter result.
 * @returns An array containing strings present in ALL non-empty input arrays, empty arrays if no
 * input, or null if intersection does not exist.
 * Calling this function on large arrays can cause the UI to freeze for a bit. Consider running
 * something similar (can't import external libs in a webworker file) in another thread via
 * webworker if synchronous execution is too slow.
 */
export const intersectionOfNonEmptyFilters = (filters: Array<string[]>): string[] | null => {
  // 1. Filter out all empty arrays (length is 0, or null/undefined entries)
  const nonEmptyFilters = filters.filter(arr => Array.isArray(arr) && arr.length > 0);

  // Edge case 1: If no arrays were provided initially.
  if (nonEmptyFilters.length === 0) {
    return [];
  }

  // Edge case 2: If only one non-empty array remains, that array is the result.
  if (nonEmptyFilters.length === 1) {
    return nonEmptyFilters[0];
  }

  // 2. Use the spread operator (...) to pass the remaining arrays as individual arguments
  //    to the intersection function. If there are no items in the arr = intersection does not exist!
  const intersectionArr = intersection(...nonEmptyFilters);
  return intersectionArr?.length ? intersectionArr : null;
};